import React from "react";
import {Autocomplete, FormLabel} from "@mui/material";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import {useSearchCustomer} from "@/service/customer.s";
import {SearchBO} from "@/service/common.s";
import isEmpty from "lodash/isEmpty";
import {BaseVO, OptionType} from "@/model/global";
import {debounce, find, findIndex} from "lodash";
import {CustomerPO} from "@/model/customer";
import {Controller, UseFormSetValue} from "react-hook-form";
import {Control} from "react-hook-form/dist/types/form";
import {SearchChanceVO, useSearchChance} from "@/service/chance.s";
import {SearchContractVO, useSearchContract, useSearchProduct} from "@/service/contract.s";
import {SearchForeheadVO, useSearchForehead} from "@/service/forehead.s";
import {SearchPlanVO, useSearchPlan} from "@/service/plan.s";
import {SearchTaskVO, useSearchTask} from "@/service/task.s";
import {PatentPO, ProductPO} from "@/model/product";
import {useSearchPatent} from "@/service/patent.s";
import {SearchMonitorVO, useSearchMonitor} from "@/service/monitor.s";
import {SearchContactVO, useSearchContact} from "@/service/contact.s";
import {SearchProjectVO, useSearchProject} from "@/service/project.s";
import {SearchPaybackVO, useSearchPayback} from "@/service/payback.s";

export interface SearchDataProps<T, R> {
    attr: string
    attrId: string
    label: string
    setValue?: UseFormSetValue<any>
    errMsg?: string
    query?: any
    autoLoad?: boolean
    required?: boolean
    readOnly?: boolean,
    freeSolo?: boolean
    enable?: boolean
    tag?: number // 1.客户 2.合同 3.商机 4.抬头 5.目标 6.任务 7.产品 8.专利 9.专利监控 10.联系人 11.项目 12.收款
    control: Control<any, Partial<T>>
    onSelect?: (item: R) => void;
    startNode?: React.ReactNode;
    endNode?: React.ReactNode;
    placeholder?: string
}

export const SearchData = <T, R>({
                                     query,
                                     label,
                                     autoLoad = false,
                                     required,
                                     freeSolo = false,
                                     readOnly,
                                     enable = true,
                                     setValue,
                                     onSelect,
                                     attrId,
                                     errMsg,
                                     placeholder,
                                     attr,
                                     control,
                                     startNode,
                                     endNode,
                                     tag = 1
                                 }: SearchDataProps<T, R>) => {
    const {mutateAsync: searchCustomerMutate, isPending: customerPending, data: searchCustomerData} = useSearchCustomer()
    const {mutateAsync: searchChanceMutate, data: searchChanceData, isPending: chancePending} = useSearchChance()
    const {mutateAsync: searchForeheadMutate, data: searchForeheadData} = useSearchForehead()
    const {mutateAsync: searchContractMutate, data: searchContractData} = useSearchContract()
    const {mutateAsync: searchPlanMutate} = useSearchPlan()
    const {mutateAsync: searchTaskMutate, data: searchTaskData} = useSearchTask()
    const [options, setOptions] = React.useState<readonly OptionType[]>([]);
    const {mutateAsync: searchProductMutate, data: searchProductData,} = useSearchProduct()
    const {mutateAsync: searchPatentMutate, data: searchPatentData,} = useSearchPatent()
    const {mutateAsync: searchMonitorMutate, data: searchMonitorData,} = useSearchMonitor()
    const {mutateAsync: searchContactMutate, data: searchContactData,} = useSearchContact()
    const {mutateAsync: searchProjectMutate, data: searchProjectData} = useSearchProject()
    const {mutateAsync: searchPaybackMutate, data: searchPaybackData} = useSearchPayback()
    const loadData = debounce(
        async (params: SearchBO, callback: (results?: readonly OptionType[]) => void) => {
            switch (tag) {
                case 1:
                    const resultCustomer: BaseVO<CustomerPO[]> = await searchCustomerMutate(params)
                    if (resultCustomer.code == 0) {
                        return callback(resultCustomer?.data?.map((item: CustomerPO) => ({label: item.customerName, value: item.id})) || [])
                    }
                    break;
                case 2:
                    const resultContract: BaseVO<SearchContractVO[]> = await searchContractMutate({...params, ...query})
                    if (resultContract.code == 0) {
                        return callback(resultContract?.data?.map((item: SearchContractVO) => ({label: item.contractName, value: item.id})) || [])
                    }
                    break
                case 3:
                    const resultChance: BaseVO<SearchChanceVO[]> = await searchChanceMutate({...params, ...query})
                    if (resultChance.code == 0) {
                        return callback(resultChance?.data?.map((item: SearchChanceVO) => ({label: item.names, value: item.id, disabled: item.contractId != 0})) || [])
                    }
                    break
                case 4:
                    const resultForehead: BaseVO<SearchForeheadVO[]> = await searchForeheadMutate(params)
                    if (resultForehead.code == 0) {
                        return callback(resultForehead?.data?.map((item: SearchForeheadVO) => ({label: item.invoiceTitle, value: item.id})) || [])
                    }
                    break
                case 5:
                    const resultPlan: BaseVO<SearchPlanVO[]> = await searchPlanMutate(params)
                    if (resultPlan.code == 0) {
                        return callback(resultPlan?.data?.map((item: SearchPlanVO) => ({label: item.planName, value: item.id})) || [])
                    }
                    break
                case 6:
                    const resultTask: BaseVO<SearchTaskVO[]> = await searchTaskMutate({...params, ...query})
                    if (resultTask.code == 0) {
                        return callback(resultTask?.data?.map((item: SearchTaskVO) => ({label: item.taskName, value: item.id})) || [])
                    }
                    break
                case 7:
                    const resultProduct: BaseVO<ProductPO[]> = await searchProductMutate({...params, ...query})
                    if (resultProduct.code == 0) {
                        return callback(resultProduct?.data?.map((item: ProductPO) => ({label: item.category, value: item.id})) || [])
                    }
                    break
                case 8:
                    const resultPatent: BaseVO<PatentPO[]> = await searchPatentMutate({...params, ...query})
                    if (resultPatent.code == 0) {
                        return callback(resultPatent?.data?.map((item: PatentPO) => ({label: item.patentName, value: item.id})) || [])
                    }
                    break
                case 9:
                    const resultMonitor: BaseVO<SearchMonitorVO[]> = await searchMonitorMutate({...params, ...query})
                    if (resultMonitor.code == 0) {
                        return callback(resultMonitor?.data?.map((item: SearchMonitorVO) => ({label: item.feeType, value: item.feeId})) || [])
                    }
                    break
                case 10:
                    const resultContact: BaseVO<SearchContactVO[]> = await searchContactMutate({...params, ...query})
                    if (resultContact.code == 0) {
                        return callback(resultContact?.data?.map((item: SearchContactVO) => ({label: item.name, value: item.id})) || [])
                    }
                    break
                case 11:
                    const resultProject: BaseVO<SearchProjectVO[]> = await searchProjectMutate({...params, ...query})
                    if (resultProject.code == 0) {
                        return callback(resultProject?.data?.map((item: SearchProjectVO) => ({label: item.category, value: item.id})) || [])
                    }
                    break
                case 12:
                    const resultPayback: BaseVO<SearchPaybackVO[]> = await searchPaybackMutate({...params, ...query})
                    if (resultPayback.code == 0) {
                        return callback(resultPayback?.data?.map((item: SearchPaybackVO) => ({label: item.customerName, value: item.id})) || [])
                    }
                    break

            }
            return callback([])
        }, 1000
    )

    const onInputChange = (keyword: string) => {
        if (!isEmpty(keyword)) {
            if (freeSolo) {
                setValue && setValue(attr, keyword)
            }
            if (findIndex(options, item => item.label == keyword) === -1) {
                buildData(keyword)
            }
        } else {
            setOptions([])
        }
    }

    const buildData = (keyword = "") => {
        enable && loadData({keyword}, (results?: readonly OptionType[]) => {
            setOptions(results!)
        })
    }

    const handleSelect = (onChange: (...event: any[]) => void, value: OptionType | null) => {
        if (onSelect) {
            switch (tag) {
                case 1:
                    const customer = find(searchCustomerData?.data, (item: CustomerPO) => item.id == value?.value)
                    onSelect && onSelect(customer as R)
                    break
                case 2:
                    const contract = find(searchContractData?.data, (item: SearchContractVO) => item.id == value?.value)
                    onSelect && onSelect(contract as R)
                    break
                case 3:
                    const chance = find(searchChanceData?.data, (item: SearchChanceVO) => item.id == value?.value)
                    onSelect && onSelect(chance as R)
                    break
                case 4:
                    const forehead = find(searchForeheadData?.data, (item: SearchForeheadVO) => item.id == value?.value)
                    onSelect && onSelect(forehead as R)
                    break
                case 6:
                    const task = find(searchTaskData?.data, (item: SearchTaskVO) => item.id == value?.value)
                    onSelect && onSelect(task as R)
                    break
                case 8:
                    const patent = find(searchPatentData?.data, (item: PatentPO) => item.id == value?.value)
                    onSelect && onSelect(patent as R)
                    break
                case 10:
                    const contact = find(searchContactData?.data, (item: SearchContactVO) => item.id == value?.value)
                    onSelect && onSelect(contact as R)
                    break
                case 11:
                    const project = find(searchProjectData?.data, (item: SearchProjectVO) => item.id == value?.value)
                    onSelect && onSelect(project as R)
                    break
                case 12:
                    const payback = find(searchPaybackData?.data, (item: SearchPaybackVO) => item.id == value?.value)
                    onSelect && onSelect(payback as R)
                    break
            }
        }
        setValue && setValue(attrId, value?.value || null)
        onChange(value?.label || null)
    }

    return <FormControl fullWidth={true} autoFocus={true} required={required}>
        {label && <FormLabel htmlFor={attr}>{label}</FormLabel>}
        <Controller control={control} name={attr}
                    render={({field: {onChange, value}}) => {
                        return <Autocomplete filterSelectedOptions={false}
                                             getOptionKey={option => option.value}
                                             loading={customerPending || chancePending}
                                             id={attr}
                                             freeSolo={freeSolo}
                                             readOnly={readOnly}
                                             getOptionDisabled={option => option.disabled}
                                             value={value || null}
                                             onFocus={() => (autoLoad && !readOnly) && buildData()}
                            // getOptionLabel={option => option.label}
                                             onChange={(event, value) => handleSelect(onChange, value)}
                                             onInputChange={(event, value) => onInputChange(value)}
                                             renderInput={(params) => <TextField
                                                 error={!!errMsg} helperText={errMsg}
                                                 placeholder={placeholder || `选择${label}`}  {...params}
                                                 slotProps={{input: {...params.InputProps, startAdornment: startNode,}}}
                                             />}
                                             options={options}/>
                    }}/>

    </FormControl>
}